import { isPlatformBrowser, NgClass } from '@angular/common';
import { Component, effect, HostListener, inject, NgZone, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ProgressSpinnerComponent } from '@banyan/commons/components/progress-spinner/progress-spinner.component';
import { HEADER_HEIGHT_PX } from '@banyan/commons/consts/global.const';
import { MatIconRegistryService } from '@banyan/commons/services/mat-icon-registry.service';
import { AccountService } from '@banyan/features/account/services/account.service';
import { BuilderService } from '@banyan/features/builder-io/services/builder.service';
import { CaptureNeedService } from '@banyan/features/capture-needs/services/capture-need.service';
import { FooterComponent } from '@banyan/features/layout/components/footer/footer.component';
import { HeaderComponent } from '@banyan/features/layout/components/header/header/header.component';
import {
  HeaderSidenavComponent,
} from '@banyan/features/layout/components/header/header-sidenav/header-sidenav.component';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { filter } from 'rxjs';

import { UtmService } from './commons/services/utm.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterOutlet, MatSidenavContainer, MatSidenav, MatSidenavContent, HeaderComponent, FooterComponent, HeaderSidenavComponent, ProgressSpinnerComponent, NgClass],
})
export class AppComponent implements OnInit {
  router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly gtmService = inject(GoogleTagManagerService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly platformId = inject(PLATFORM_ID);
  metaService = inject(Meta);
  private readonly utmService = inject(UtmService);

  @ViewChild('sidenav') sidenav: MatSidenav | undefined;
  title = 'banyan-front';
  showHead: boolean = true;
  isScrolled: boolean = false;
  accountService = inject(AccountService);
  captureNeedService = inject(CaptureNeedService);
  builderService = inject(BuilderService);
  matIconRegistryService = inject(MatIconRegistryService);
  ngZone = inject(NgZone);
  showAdminLayout: boolean = true;

  constructor() {
    const router = this.router;

    if (isPlatformBrowser(this.platformId)) {
      this.matIconRegistryService.registerIcons();
    }
    this.accountService.setInitLocale();
    effect(() => {
      if (this.captureNeedService.languageChanged()) {
        this.translateService.use(this.accountService.user().preferredLanguage);
        this.captureNeedService.languageChanged.set(false);
      }
      if (!this.accountService.user().profile) {
        if (isPlatformBrowser(this.platformId)) {
          this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
              this.gtmService.pushTag({
                event: 'userAuthenticated',
                userId: 20002,
                userName: 'Givaudan Not Connected',
                userEmail: 'appcues@givaudan.com',
              }).then();
            }, 800);
          });
        }
      }
    });

    router.events.subscribe(() => {
      this.showAdminLayout = this.router.url.startsWith('/admin');
    });
  }

  ngOnInit(): void {
    this.updateShowHead(this.getFullPath(this.activatedRoute));
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: any) => {
      this.updateShowHead(event.urlAfterRedirects);
      const gtmTag = {
        event: 'page',
        pageName: event.urlAfterRedirects,
      };
      this.gtmService.pushTag(gtmTag).then();
      if (isPlatformBrowser(this.platformId)) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
    this.translateService.use(this.accountService.user().preferredLanguage);
    this.addCanonicalUrlLink(environment.domain);
  }

  updateShowHead(url: string): void {
    this.showHead = !url.includes('/protected/capture-needs');
  }

  getFullPath(route: ActivatedRoute): string {
    let path = '';
    while (route.firstChild) {
      route = route.firstChild;
    }
    if (route.snapshot.url) {
      path = route.snapshot.url.map(segment => segment.path).join('/');
    }
    return '/' + path;
  }

  addCanonicalUrlLink(url: string) {
    this.metaService.updateTag({ rel: 'canonical', href: url });
  }

  closeSidenav() {
    this.sidenav?.close();
  }

  @HostListener('window:scroll', [])
  onScroll() {
    if (isPlatformBrowser(this.platformId)) {
      const scrollTopPosition =
        document.documentElement.scrollTop || document.body.scrollTop || 0;
      this.isScrolled = scrollTopPosition > HEADER_HEIGHT_PX;
    }
  }
}
